/**
 * TestTab: 비디오 수신 해상도 정책 테스트 탭
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { uiActions } from '@/store/slices/uiSlice';
import {
  RESOLUTION_OPTIONS,
  getGridResolution,
  FOCUS_VIEW_POLICY,
  DEFAULT_GRID_TIERS,
  DEFAULT_GRID_FALLBACK,
  type VideoQualityOverride,
  type VideoQualityLevel,
  type GridTier,
} from '@/conference/config/videoQualityPolicy';

// ─── 공통 스타일 ─────────────────────────────────────────────────────────────

const S = {
  table: { width: '100%', borderCollapse: 'collapse' as const, fontSize: 13, lineHeight: '20px' },
  th: { border: '1px solid #e1e1e1', padding: '8px 12px', background: '#f5f6f8', color: '#555', fontWeight: 600 as const, textAlign: 'left' as const, whiteSpace: 'nowrap' as const },
  td: { border: '1px solid #e1e1e1', padding: '8px 12px', color: '#333', textAlign: 'left' as const, verticalAlign: 'middle' as const },
  tdActive: { background: 'rgba(16, 90, 255, 0.05)', fontWeight: 600 as const },
  sectionTitle: { fontSize: 14, fontWeight: 700 as const, color: '#333', margin: '0 0 10px 0' },
  divider: { borderTop: '1px solid #ededed', marginTop: 20, paddingTop: 20 },
  badge: { display: 'inline-block', fontSize: 11, color: '#e0590e', background: '#fff4ec', borderRadius: 4, padding: '1px 6px', marginLeft: 6, fontWeight: 600 as const },
  hint: { fontSize: 12, color: '#888', background: '#f7f8fa', borderRadius: 6, padding: '10px 12px', margin: '0 0 16px 0', lineHeight: '18px' },
  btnWrap: { display: 'flex', gap: 8, justifyContent: 'flex-end' as const, marginTop: 20 },
  iconBtn: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 26, height: 26, border: '1px solid #dcdfe3', borderRadius: 4, background: '#fff', cursor: 'pointer', fontSize: 14, color: '#555', lineHeight: 1 },
  input: { width: 60, height: 30, padding: '0 8px', border: '1px solid #dcdfe3', borderRadius: 6, fontSize: 13, color: '#333', textAlign: 'center' as const },
} as const;

// ─── 인라인 드롭다운 (td 안에서 사용) ───────────────────────────────────────

interface CellDropdownProps {
  value: VideoQualityLevel | null;
  onChange: (v: VideoQualityLevel | null) => void;
  policyDefault: number;
  /** '자동' 옵션을 표시할지 (기본 true) */
  showAuto?: boolean;
}

const CellDropdown: React.FC<CellDropdownProps> = ({ value, onChange, policyDefault, showAuto = true }) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  const options = showAuto ? RESOLUTION_OPTIONS : RESOLUTION_OPTIONS.filter((o) => o.value != null);
  const displayLabel = value == null
    ? (showAuto ? `자동 (${policyDefault}p)` : `${policyDefault}p`)
    : (RESOLUTION_OPTIONS.find((o) => o.value === value)?.label ?? `${value}p`);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setIsOpen((p) => !p)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          width: '100%', minWidth: 140, height: 30, padding: '0 10px',
          border: `1px solid ${isOpen ? '#105aff' : '#dcdfe3'}`, borderRadius: 6,
          background: '#fff', fontSize: 13, color: '#333', cursor: 'pointer',
        }}
      >
        <span>{displayLabel}</span>
        <span style={{ fontSize: 10, color: '#999', marginLeft: 4 }}>▾</span>
      </button>
      {isOpen && (
        <ul style={{
          position: 'absolute', top: '100%', left: 0, right: 0, margin: '4px 0 0', padding: 4,
          background: '#fff', border: '1px solid #e1e1e1', borderRadius: 8,
          boxShadow: '0 4px 12px rgba(0,0,0,.1)', listStyle: 'none', zIndex: 10,
        }}>
          {options.map((opt) => {
            const isSelected = value === opt.value;
            return (
              <li key={String(opt.value)}>
                <button
                  type="button"
                  onClick={() => { onChange(opt.value); setIsOpen(false); }}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    width: '100%', height: 30, padding: '0 8px', border: 0, borderRadius: 4,
                    background: isSelected ? '#f0f4ff' : 'transparent',
                    fontSize: 13, color: isSelected ? '#105aff' : '#333',
                    fontWeight: isSelected ? 600 : 400, cursor: 'pointer', textAlign: 'left',
                  }}
                >
                  <span>{opt.value == null ? `자동 (${policyDefault}p)` : opt.label}</span>
                  {isSelected && <span style={{ color: '#105aff', fontSize: 13 }}>✓</span>}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

// ─── 메인 TestTab ──────────────────────────────────────────────────────────

const SCREEN_MODE_LABELS: Record<string, string> = { GRID: '그리드뷰', PRESENTER: '발표자뷰', SIDE: '사이드뷰' };

const MIN_TIERS = 1; // "그 이상" 포함하면 최소 2개 행 (임계값 1 + 폴백 1)

const TestTab: React.FC = () => {
  const dispatch = useAppDispatch();
  const screenMode = useAppSelector((s) => s.ui.screenMode);
  const maxGridSize = useAppSelector((s) => s.ui.maxGridSize);
  const savedOverride = useAppSelector((s) => s.ui.videoQualityOverride);
  const remoteCount = useAppSelector((s) => s.participants.participantIds.length);

  // ── draft 상태 ──
  const [draft, setDraft] = useState<VideoQualityOverride>({
    grid: null, presenterMain: null, presenterSub: null,
    sideMain: null, sideSub: null,
    gridTiers: null, gridFallback: null,
    ...savedOverride,
  });

  // 그리드 정책 편집용 로컬 상태
  const [tiersDraft, setTiersDraft] = useState<GridTier[]>(() =>
    savedOverride?.gridTiers ?? [...DEFAULT_GRID_TIERS],
  );
  const [fallbackDraft, setFallbackDraft] = useState<VideoQualityLevel>(() =>
    savedOverride?.gridFallback ?? DEFAULT_GRID_FALLBACK,
  );

  useEffect(() => {
    if (!savedOverride) {
      setDraft({ grid: null, presenterMain: null, presenterSub: null, sideMain: null, sideSub: null, gridTiers: null, gridFallback: null });
      setTiersDraft([...DEFAULT_GRID_TIERS]);
      setFallbackDraft(DEFAULT_GRID_FALLBACK);
    }
  }, [savedOverride]);

  const updateDraft = useCallback(<K extends keyof VideoQualityOverride>(key: K, val: VideoQualityOverride[K]) => {
    setDraft((prev) => ({ ...prev, [key]: val }));
  }, []);

  // ── 그리드 정책 tier 조작 ──
  const addTier = useCallback(() => {
    setTiersDraft((prev) => {
      const lastMax = prev.length > 0 ? prev[prev.length - 1].maxCount : 0;
      return [...prev, { maxCount: lastMax + 5, maxHeight: 360 as VideoQualityLevel }];
    });
  }, []);

  const removeTier = useCallback((idx: number) => {
    setTiersDraft((prev) => {
      if (prev.length <= MIN_TIERS) return prev;
      return prev.filter((_, i) => i !== idx);
    });
  }, []);

  const updateTierCount = useCallback((idx: number, val: number) => {
    setTiersDraft((prev) => prev.map((t, i) => i === idx ? { ...t, maxCount: Math.max(1, val) } : t));
  }, []);

  const updateTierHeight = useCallback((idx: number, val: VideoQualityLevel) => {
    setTiersDraft((prev) => prev.map((t, i) => i === idx ? { ...t, maxHeight: val } : t));
  }, []);

  // ── 적용/초기화 ──
  const handleApply = useCallback(() => {
    // 정렬 보장
    const sortedTiers = [...tiersDraft].sort((a, b) => a.maxCount - b.maxCount);

    const isDefaultTiers = sortedTiers.length === DEFAULT_GRID_TIERS.length
      && sortedTiers.every((t, i) => t.maxCount === DEFAULT_GRID_TIERS[i].maxCount && t.maxHeight === DEFAULT_GRID_TIERS[i].maxHeight);
    const isDefaultFallback = fallbackDraft === DEFAULT_GRID_FALLBACK;

    const newDraft: VideoQualityOverride = {
      ...draft,
      gridTiers: (isDefaultTiers && isDefaultFallback) ? null : sortedTiers,
      gridFallback: (isDefaultTiers && isDefaultFallback) ? null : fallbackDraft,
    };

    const hasOverride = Object.values(newDraft).some((v) => v != null);
    if (hasOverride) {
      dispatch(uiActions.setVideoQualityOverride(newDraft));
    } else {
      dispatch(uiActions.clearVideoQualityOverride());
    }
  }, [dispatch, draft, tiersDraft, fallbackDraft]);

  const handleReset = useCallback(() => {
    setDraft({ grid: null, presenterMain: null, presenterSub: null, sideMain: null, sideSub: null, gridTiers: null, gridFallback: null });
    setTiersDraft([...DEFAULT_GRID_TIERS]);
    setFallbackDraft(DEFAULT_GRID_FALLBACK);
    dispatch(uiActions.clearVideoQualityOverride());
  }, [dispatch]);

  // ── 계산값 ──
  const visibleCountGrid = Math.min(remoteCount + 1, maxGridSize);
  const gridPolicyDefault = getGridResolution(visibleCountGrid, tiersDraft, fallbackDraft);
  const isOverrideActive = savedOverride != null;

  // 정책 텍스트 생성
  const policyText = (() => {
    const activeTiers = savedOverride?.gridTiers ?? DEFAULT_GRID_TIERS;
    const activeFallback = savedOverride?.gridFallback ?? DEFAULT_GRID_FALLBACK;
    const parts = activeTiers.map((t) => `≤${t.maxCount}명: ${t.maxHeight}p`);
    parts.push(`${(activeTiers.length > 0 ? activeTiers[activeTiers.length - 1].maxCount + 1 : 1)}명+: ${activeFallback}p`);
    return parts.join(' / ');
  })();

  return (
    <>
      {/* ── 1. 현재 상태 ── */}
      <p style={S.sectionTitle}>현재 상태</p>
      <table style={S.table}>
        <tbody>
          <tr>
            <td style={{ ...S.th, width: 100 }}>현재 뷰</td>
            <td style={S.td}><strong>{SCREEN_MODE_LABELS[screenMode] ?? screenMode}</strong></td>
          </tr>
          <tr>
            <td style={{ ...S.th, width: 100 }}>참여자 수</td>
            <td style={S.td}>
              <strong>{remoteCount + 1}명</strong>
              <span style={{ color: '#888', marginLeft: 6 }}>(원격 {remoteCount} + 본인)</span>
            </td>
          </tr>
          <tr>
            <td style={{ ...S.th, width: 100 }}>적용 정책</td>
            <td style={S.td}>
              {screenMode === 'GRID'
                ? `그리드 ${visibleCountGrid}타일 → ${gridPolicyDefault}p`
                : `메인 ${FOCUS_VIEW_POLICY.main}p / 서브 ${FOCUS_VIEW_POLICY.sub}p`}
              {isOverrideActive && <span style={S.badge}>오버라이드</span>}
            </td>
          </tr>
        </tbody>
      </table>

      {/* ── 2. 그리드 정책 편집 ── */}
      <div style={S.divider}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <p style={{ ...S.sectionTitle, margin: 0 }}>그리드뷰 정책 편집</p>
          <button type="button" style={S.iconBtn} title="구간 추가" onClick={addTier}>＋</button>
        </div>
        <p style={S.hint}>
          참여자 수 구간별 수신 해상도를 설정합니다. 구간은 오름차순으로 자동 정렬됩니다.
        </p>
        <table style={S.table}>
          <thead>
            <tr>
              <th style={{ ...S.th, width: 40, textAlign: 'center' }}>#</th>
              <th style={S.th}>조건</th>
              <th style={{ ...S.th, width: 160 }}>해상도</th>
              <th style={{ ...S.th, width: 40, textAlign: 'center' }}>&nbsp;</th>
            </tr>
          </thead>
          <tbody>
            {tiersDraft.map((tier, idx) => (
              <tr key={idx}>
                <td style={{ ...S.td, textAlign: 'center', color: '#999' }}>{idx + 1}</td>
                <td style={S.td}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ color: '#888' }}>≤</span>
                    <input
                      type="number"
                      min={1}
                      value={tier.maxCount}
                      onChange={(e) => updateTierCount(idx, parseInt(e.target.value, 10) || 1)}
                      style={S.input}
                    />
                    <span style={{ color: '#888' }}>명</span>
                  </span>
                </td>
                <td style={{ ...S.td, position: 'relative' as const }}>
                  <CellDropdown
                    value={tier.maxHeight}
                    onChange={(v) => v != null && updateTierHeight(idx, v)}
                    policyDefault={tier.maxHeight}
                    showAuto={false}
                  />
                </td>
                <td style={{ ...S.td, textAlign: 'center' }}>
                  {tiersDraft.length > MIN_TIERS && (
                    <button type="button" style={{ ...S.iconBtn, color: '#e05050', borderColor: '#f0c0c0' }} onClick={() => removeTier(idx)}>✕</button>
                  )}
                </td>
              </tr>
            ))}
            {/* 폴백 (그 이상) 행 */}
            <tr style={{ background: '#fafbfc' }}>
              <td style={{ ...S.td, textAlign: 'center', color: '#999' }}>—</td>
              <td style={{ ...S.td, color: '#888' }}>
                {tiersDraft.length > 0
                  ? `${tiersDraft[tiersDraft.length - 1].maxCount + 1}명 이상`
                  : '전체'}
              </td>
              <td style={{ ...S.td, position: 'relative' as const }}>
                <CellDropdown
                  value={fallbackDraft}
                  onChange={(v) => v != null && setFallbackDraft(v)}
                  policyDefault={fallbackDraft}
                  showAuto={false}
                />
              </td>
              <td style={{ ...S.td, textAlign: 'center', color: '#ccc', fontSize: 11 }}>고정</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* ── 3. 뷰별 해상도 오버라이드 ── */}
      <div style={S.divider}>
        <p style={S.sectionTitle}>발표자/사이드뷰 해상도 오버라이드</p>
        <p style={S.hint}>
          발표자·사이드뷰의 수신 해상도를 강제로 고정합니다. &quot;자동&quot;은 기본 정책 값을 따릅니다.
        </p>
        <table style={S.table}>
          <thead>
            <tr>
              <th style={{ ...S.th, width: 90 }}>뷰 모드</th>
              <th style={{ ...S.th, width: 80 }}>대상</th>
              <th style={S.th}>해상도</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={{ ...S.td, fontWeight: 600 }} rowSpan={2}>발표자뷰</td>
              <td style={S.td}>메인</td>
              <td style={S.td}>
                <CellDropdown
                  value={draft.presenterMain}
                  onChange={(v) => updateDraft('presenterMain', v)}
                  policyDefault={FOCUS_VIEW_POLICY.main}
                />
              </td>
            </tr>
            <tr>
              <td style={S.td}>서브</td>
              <td style={S.td}>
                <CellDropdown
                  value={draft.presenterSub}
                  onChange={(v) => updateDraft('presenterSub', v)}
                  policyDefault={FOCUS_VIEW_POLICY.sub}
                />
              </td>
            </tr>
            <tr>
              <td style={{ ...S.td, fontWeight: 600 }} rowSpan={2}>사이드뷰</td>
              <td style={S.td}>메인</td>
              <td style={S.td}>
                <CellDropdown
                  value={draft.sideMain}
                  onChange={(v) => updateDraft('sideMain', v)}
                  policyDefault={FOCUS_VIEW_POLICY.main}
                />
              </td>
            </tr>
            <tr>
              <td style={S.td}>서브</td>
              <td style={S.td}>
                <CellDropdown
                  value={draft.sideSub}
                  onChange={(v) => updateDraft('sideSub', v)}
                  policyDefault={FOCUS_VIEW_POLICY.sub}
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* ── 4. 버튼 ── */}
      <div style={S.btnWrap}>
        <button type="button" className="common_round_btn rtcNew_btnType blue" onClick={handleApply}>적용</button>
        <button type="button" className="common_round_btn rtcNew_btnType" onClick={handleReset}>기본값 복원</button>
      </div>
    </>
  );
};

export default TestTab;
